import { useEffect, useRef, useState } from 'react';
import interact from 'interactjs';
import { Maximize2, Minus, Square, X } from 'lucide-react';
import { motion } from 'motion/react';
import { Button } from 'react-aria-components';

import type { AppDefinition, Bounds, ContextMenuState, WindowState } from '../app/types';

interface WindowFrameProps {
  state: WindowState;
  definition: AppDefinition;
  active: boolean;
  desktopHidden: boolean;
  children: React.ReactNode;
  onFocus: () => void;
  onClose: () => void;
  onMinimize: () => void;
  onMaximize: () => void;
  onSnap: (bounds: Bounds) => void;
  onBounds: (bounds: Bounds) => void;
  onContextMenu: (menu: ContextMenuState) => void;
}

export function WindowFrame(props: WindowFrameProps) {
  const { state, definition, active, desktopHidden, children, onFocus, onClose, onMinimize, onMaximize, onSnap, onBounds, onContextMenu } = props;
  const ref = useRef<HTMLDivElement>(null);
  const [snapOpen, setSnapOpen] = useState(false);
  const hidden = desktopHidden || state.minimized;
  const boundsRef = useRef(state.bounds);
  boundsRef.current = state.bounds;

  useEffect(() => {
    const element = ref.current;
    if (!element || state.maximized || state.minimized) return;
    const interaction = interact(element)
      .draggable({
        allowFrom: '.window-titlebar-drag',
        ignoreFrom: 'button,input,textarea,select',
        listeners: {
          move(event) {
            const x = Number(element.dataset.dragX || 0) + event.dx;
            const y = Number(element.dataset.dragY || 0) + event.dy;
            element.dataset.dragX = String(x);
            element.dataset.dragY = String(y);
            element.style.transform = `translate(${x}px, ${y}px)`;
          },
          end() {
            const dx = Number(element.dataset.dragX || 0);
            const dy = Number(element.dataset.dragY || 0);
            element.style.transform = '';
            element.dataset.dragX = '0';
            element.dataset.dragY = '0';
            const next = { ...boundsRef.current, x: Math.max(0, boundsRef.current.x + dx), y: Math.max(0, boundsRef.current.y + dy) };
            if (next.y < 8) onMaximize();
            else if (next.x < 8) onBounds({ x: 0, y: 0, width: Math.floor(window.innerWidth / 2), height: window.innerHeight - 56 });
            else if (next.x + next.width > window.innerWidth - 8) onBounds({ x: Math.floor(window.innerWidth / 2), y: 0, width: Math.ceil(window.innerWidth / 2), height: window.innerHeight - 56 });
            else onBounds(next);
          }
        }
      })
      .resizable({
        edges: { left: true, right: true, bottom: true, top: true },
        margin: 7,
        modifiers: [interact.modifiers.restrictSize({ min: { width: 320, height: 220 } })],
        listeners: {
          move(event) {
            const dx = Number(element.dataset.dragX || 0) + event.deltaRect.left;
            const dy = Number(element.dataset.dragY || 0) + event.deltaRect.top;
            element.dataset.dragX = String(dx);
            element.dataset.dragY = String(dy);
            element.style.width = `${event.rect.width}px`;
            element.style.height = `${event.rect.height}px`;
            element.style.transform = `translate(${dx}px, ${dy}px)`;
            element.dataset.resizeWidth = String(event.rect.width);
            element.dataset.resizeHeight = String(event.rect.height);
          },
          end() {
            const next = {
              x: Math.max(0, boundsRef.current.x + Number(element.dataset.dragX || 0)),
              y: Math.max(0, boundsRef.current.y + Number(element.dataset.dragY || 0)),
              width: Number(element.dataset.resizeWidth || boundsRef.current.width),
              height: Number(element.dataset.resizeHeight || boundsRef.current.height)
            };
            element.style.transform = '';
            element.dataset.dragX = '0';
            element.dataset.dragY = '0';
            onBounds(next);
          }
        }
      });
    return () => interaction.unset();
  }, [onBounds, onMaximize, state.maximized, state.minimized]);

  useEffect(() => {
    const element = ref.current;
    const focused = document.activeElement;
    if (hidden && element && focused instanceof HTMLElement && element.contains(focused)) focused.blur();
  }, [hidden]);

  const Icon = definition.icon;
  return (
    <motion.div
      ref={ref}
      className={`window-frame ${active ? 'active' : ''} ${state.maximized ? 'maximized' : ''} ${desktopHidden ? 'desktop-hidden' : ''} ${state.minimized ? 'minimized' : ''}`}
      aria-hidden={hidden}
      inert={hidden}
      data-window-id={state.id}
      data-app-id={state.appId}
      initial={{ opacity: 0, scale: 0.985 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={{ duration: 0.14 }}
      style={state.maximized ? { zIndex: state.zIndex } : { left: state.bounds.x, top: state.bounds.y, width: state.bounds.width, height: state.bounds.height, zIndex: state.zIndex }}
      onPointerDown={onFocus}
      onContextMenu={event => {
        event.preventDefault();
        onContextMenu({ x: event.clientX, y: event.clientY, actions: [
          { id: 'restore', label: state.maximized ? 'Wiederherstellen' : 'Maximieren', icon: Maximize2, run: onMaximize },
          { id: 'minimize', label: 'Minimieren', icon: Minus, run: onMinimize },
          { id: 'sep', label: '', separator: true, run: () => {} },
          { id: 'close', label: 'Schließen', icon: X, danger: true, run: onClose }
        ] });
      }}
    >
      <header className="window-titlebar">
        <div className="window-titlebar-drag" onDoubleClick={onMaximize}><Icon style={{ color: definition.accent }} /><span>{state.title}</span></div>
        <div className="window-controls">
          <Button aria-label="Minimieren" onPress={onMinimize}><Minus /></Button>
          <div className="maximize-control" onPointerEnter={() => setSnapOpen(true)} onPointerLeave={() => setSnapOpen(false)}>
            <Button aria-label={state.maximized ? 'Wiederherstellen' : 'Maximieren'} aria-expanded={snapOpen} onPress={onMaximize}>{state.maximized ? <Square /> : <Maximize2 />}</Button>
            {snapOpen && <div className="snap-layout" role="menu" aria-label="Snap-Layouts">
              <button role="menuitem" aria-label="Linke Bildschirmhälfte" onClick={() => { onSnap({ x: 0, y: 0, width: Math.floor(window.innerWidth / 2), height: window.innerHeight - 64 }); setSnapOpen(false); }}><span /><span /></button>
              <button role="menuitem" aria-label="Rechte Bildschirmhälfte" onClick={() => { onSnap({ x: Math.floor(window.innerWidth / 2), y: 0, width: Math.ceil(window.innerWidth / 2), height: window.innerHeight - 64 }); setSnapOpen(false); }}><span /><span /></button>
              <button role="menuitem" aria-label="Oben links" onClick={() => { onSnap({ x: 0, y: 0, width: Math.floor(window.innerWidth / 2), height: Math.floor((window.innerHeight - 64) / 2) }); setSnapOpen(false); }}><span /><span /><span /><span /></button>
              <button role="menuitem" aria-label="Oben rechts" onClick={() => { onSnap({ x: Math.floor(window.innerWidth / 2), y: 0, width: Math.ceil(window.innerWidth / 2), height: Math.floor((window.innerHeight - 64) / 2) }); setSnapOpen(false); }}><span /><span /><span /><span /></button>
            </div>}
          </div>
          <Button aria-label="Schließen" className="close" onPress={onClose}><X /></Button>
        </div>
      </header>
      <div className="window-content">{children}</div>
    </motion.div>
  );
}
